// @ts-check

/**
 * @file Renders the invoice group detail route.
 */

import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import {useLocalSearchParams} from "expo-router"
import {memo, useEffect} from "react"
import {shapeComponent, ShapeComponent} from "set-state-compare/build/shape-component.js"
import {View} from "react-native-propforge"
import Card from "../../../src/components/card"
import InvoiceCard from "../../../src/components/invoices/card"
import {InvoiceGroupLink} from "../../../src/frontend-models/invoice-group-link"
import {InvoiceGroup} from "../../../src/frontend-models/invoice-group"
import Pagination from "../../../src/components/pagination"
import errorMessage from "../../../src/shared/error-message"
import {requiredRouteParam} from "../../../src/shared/route-params"
import useThemeColors, {useThemeName} from "../../../src/shared/use-theme-colors"
import Text from "@kaspernj/api-maker/build/utils/text"
import {useScreenLoading} from "../../../src/components/board-layout/screen-loading"
import ScreenTitle from "../../../src/components/screen-title"

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @type {Record<string, object>} */
const styles = {}
const INVOICE_CARD_PRELOAD_SELECT = {
  Invoice: ["amountCents", "amountCurrency", "creditorId", "date", "id", "invoiceNo", "invoiceType", "organizationId", "state", "title", "translatedInvoiceType", "translatedState"],
  InvoiceGroupLink: ["id", "invoiceGroupId", "invoiceId"],
  Organization: ["id", "name"]
}
const INVOICES_PAGE_SIZE = 30

/** @typedef {import("../../../src/frontend-models/invoice").default} Invoice */
/** @typedef {{[key: string]: unknown}} InvoiceGroupsShowScreenProps */

/**
 * @typedef {object} InvoiceGroupsShowScreenState
 * @property {InvoiceGroup | null} invoiceGroup Loaded invoice group record.
 * @property {Invoice[]} invoices Loaded linked invoices.
 * @property {boolean} invoicesLoading Whether invoice cards are loading.
 * @property {number} invoicesPage Current invoice page.
 * @property {number} invoicesTotalCount Total linked invoice count.
 * @property {boolean} loading Whether invoice group details are loading.
 * @property {{kind: "error", text: string} | null} status Screen status message.
 */

/**
 * Invoice group show screen.
 * @augments {ShapeComponent<InvoiceGroupsShowScreenProps, InvoiceGroupsShowScreenState>}
 */
class InvoiceGroupsShowScreen extends ShapeComponent {
  loadInvoicesRequestId = 0
  /** @type {string} */
  invoiceGroupID
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()
  /** @type {InvoiceGroupsShowScreenState} */
  state = {
    invoiceGroup: /** @type {InvoiceGroup | null} */ null,
    invoices: [],
    invoicesLoading: true,
    invoicesPage: 1,
    invoicesTotalCount: 0,
    loading: true,
    status: /** @type {{kind: "error", text: string} | null} */ null
  }

  /** Initializes route params and invoice group loading. */
  setup() {
    useLocale()
    useScreenLoading(this.s.loading)
    this.colors = useThemeColors()
    this.themeName = useThemeName()
    const params =
      /** @type {Record<string, string | string[]>} */ useLocalSearchParams()

    const invoiceGroupID = requiredRouteParam(params, "id", "Invoice group ID")
    this.invoiceGroupID = invoiceGroupID
    useEffect(() => {
      let cancelled = false
      const loadData = async () => {
        try {
          const invoiceGroup = await InvoiceGroup.find(invoiceGroupID)
          if (cancelled) return
          this.s.invoiceGroup = invoiceGroup
          this.s.loading = false
        } catch (error) {
          if (cancelled) return
          this.s.loading = false
          this.s.status = {
            kind: "error",
            text: errorMessage(error)
          }
        }
      }
      void loadData()
      return () => {
        cancelled = true
      }
    }, [invoiceGroupID])
    const invoicesPage = this.s.invoicesPage
    useEffect(() => {
      void this.tt.loadInvoices(invoiceGroupID, invoicesPage)
    }, [invoiceGroupID, invoicesPage])
  }

  /**
   * Renders invoice group details.
   * @returns {ReactNode} Invoice group detail content.
   */
  render() {
    const {invoiceGroup, loading, status} = this.s
    const screenTitle = invoiceGroup ? invoiceGroup.name() : _("Invoice groups")
    if (loading) {
      return (
        <View
          style={styles.loadingScreen ||= {flex: 1}}
          testID="invoiceGroupShowScreen"
        />
      )
    }
    return (
      <View
        style={styles.screen ||= {
          flex: 1
        }}
        testID="invoiceGroupShowScreen"
      >
        <ScreenTitle>{screenTitle}</ScreenTitle>
        <View
          testID="invoiceGroupShowScreen/content"
        >
          {status &&
            <Text
              style={styles[`statusError-${this.tt.themeName}`] ||= {
                color: this.tt.colors.dangerText,
                fontSize: 14,
                fontWeight: 600,
                marginBottom: 16
              }}
              testID="invoiceGroupShowScreen/status"
            >
              {status.text}
            </Text>
          }
          {invoiceGroup &&
            <View
              testID="invoiceGroupShowScreen/body"
            >
              <Card
                style={styles.cardSpacing ||= {
                  padding: 20
                }}
                testID="invoiceGroupShowScreen/card"
              >
                <Text
                  style={styles[`title-${this.tt.themeName}`] ||= {
                    fontSize: 24,
                    fontWeight: 700,
                    marginBottom: 16
                  }}
                  testID="invoiceGroupShowScreen/title"
                >
                  {screenTitle}
                </Text>
                <View
                  style={styles[`detailRow-${this.tt.themeName}`] ||= {
                    borderBottomColor: this.tt.colors.divider,
                    borderBottomWidth: 1,
                    paddingVertical: 10
                  }}
                  testID="invoiceGroupShowScreen/name/row"
                >
                  <Text
                    style={styles[`detailLabel-${this.tt.themeName}`] ||= {
                      color: this.tt.colors.textMuted,
                      fontSize: 14,
                      fontWeight: 600,
                      marginBottom: 4
                    }}
                    testID="invoiceGroupShowScreen/name/label"
                  >
                    {_("Name")}
                  </Text>
                  <Text
                    style={styles[`detailValue-${this.tt.themeName}`] ||= {
                      fontSize: 16
                    }}
                    testID="invoiceGroupShowScreen/name/value"
                  >
                    {invoiceGroup.name() || "-"}
                  </Text>
                </View>
              </Card>
              {this.renderInvoicesSection()}
            </View>
          }
        </View>
      </View>
    )
  }

  /**
   * Renders linked invoices for this invoice group.
   * @returns {ReactNode} Invoice cards section.
   */
  renderInvoicesSection() {
    const {invoices, invoicesLoading, invoicesPage, invoicesTotalCount} = this.s
    return (
      <View
        style={styles.invoicesSection ||= {
          marginTop: 24
        }}
        testID="invoiceGroupShowScreen/invoicesSection"
      >
        <Text
          style={styles[`sectionTitle-${this.tt.themeName}`] ||= {
            fontSize: 18,
            fontWeight: 700,
            marginBottom: 12
          }}
          testID="invoiceGroupShowScreen/invoicesSection/title"
        >
          {_("Invoices")}
        </Text>
        {invoicesLoading &&
          <Text
            style={styles[`invoicesLoadingText-${this.tt.themeName}`] ||= {
              color: this.tt.colors.textMuted,
              fontWeight: 600,
              marginBottom: 12
            }}
            testID="invoiceGroupShowScreen/invoicesSection/loadingText"
          >
            {_("Loading invoices...")}
          </Text>
        }
        {!invoicesLoading && invoices.length === 0 &&
          <Card
            style={styles.emptyInvoicesCard ||= {
              alignItems: "center",
              paddingVertical: 32
            }}
            testID="invoiceGroupShowScreen/invoicesSection/emptyCard"
          >
            <Text
              style={styles[`emptyInvoicesText-${this.tt.themeName}`] ||= {
                color: this.tt.colors.textMuted,
                fontWeight: 600
              }}
              testID="invoiceGroupShowScreen/invoicesSection/emptyCard/text"
            >
              {_("No invoices were linked to this invoice group")}
            </Text>
          </Card>
        }
        {!invoicesLoading && invoices.length > 0 &&
          <View testID="invoiceGroupShowScreen/invoicesSection/list">
            {invoices.map((invoice) =>
              <InvoiceCard
                key={invoice.id()}
                invoice={invoice}
                testID={`invoiceGroupShowScreen/invoicesSection/row-${invoice.id()}`}
              />
            )}
          </View>
        }
        <Pagination
          disabled={invoicesLoading}
          onPageChange={this.tt.onInvoicesPageChange}
          page={invoicesPage}
          pageSize={INVOICES_PAGE_SIZE}
          testID="invoiceGroupShowScreen/invoicesSection/pagination"
          totalCount={invoicesTotalCount}
        />
      </View>
    )
  }

  /**
   * Applies a new invoice page number.
   * @param {number} nextPage Page to load.
   * @returns {void} Nothing.
   */
  onInvoicesPageChange = (nextPage) => {
    if (nextPage === this.s.invoicesPage) return
    this.s.invoicesPage = nextPage
  }

  /**
   * Loads the current page of invoices linked to the invoice group.
   * @param {string} invoiceGroupID Invoice group whose invoices should load.
   * @param {number} page 1-based page number.
   * @returns {Promise<void>} Resolves when invoice loading completes.
   */
  loadInvoices = async (invoiceGroupID, page) => {
    const requestId = ++this.loadInvoicesRequestId
    this.s.invoicesLoading = true
    try {
      const baseQuery = InvoiceGroupLink
        .where({invoiceGroupId: invoiceGroupID})
        .order([["createdAt", "desc"], ["id", "desc"]])
      const [links, totalCount] = await Promise.all([
        baseQuery
          .clone()
          .preload({invoice: {creditor: true, organization: true}})
          .select(INVOICE_CARD_PRELOAD_SELECT)
          .page(page)
          .perPage(INVOICES_PAGE_SIZE)
          .toArray(),
        baseQuery.clone().count()
      ])
      if (this.loadInvoicesRequestId !== requestId) return
      const totalPages = Math.max(1, Math.ceil(totalCount / INVOICES_PAGE_SIZE))
      if (page > totalPages) {
        this.s.invoicesLoading = false
        this.s.invoicesTotalCount = totalCount
        this.s.invoicesPage = totalPages
        return
      }
      this.s.invoices = links
        .map((link) => {
          const invoice = link.invoice()
          if (!invoice) return null
          return invoice
        })
        .filter(
          /**
           * Keeps loaded invoice rows.
           * @param {Invoice | null} invoice Invoice row candidate.
           * @returns {invoice is Invoice} True when invoice row is loaded.
           */
          (invoice) =>
            invoice !== null
        )
      this.s.invoicesLoading = false
      this.s.invoicesTotalCount = totalCount
    } catch (error) {
      if (this.loadInvoicesRequestId !== requestId) return
      this.s.invoices = []
      this.s.invoicesLoading = false
      this.s.invoicesTotalCount = 0
      this.s.status = {
        kind: "error",
        text: errorMessage(error)
      }
    }
  }
}

/** @type {NamedExoticComponent<InvoiceGroupsShowScreenProps>} */
const InvoiceGroupsShowScreenComponent = memo(shapeComponent(InvoiceGroupsShowScreen))

export default InvoiceGroupsShowScreenComponent
